import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import {
  AddMfaBackup,
  InitiateAuthentication,
  LoginOAuthParams,
  SubmitUserRegistration,
  SwapAuthForAccessTokens,
  SwapAuthForAccessTokensParams,
  UserRegistrationInfo
} from '../../../models';
import { MultiFactorAuthenticationUtilsService } from '../../../utils/multi-factor-authentication-utils.service';
import { AddBackupMfaService } from '../../multi-factor-authentication-adapter-service/add-backup-mfa/add-backup-mfa.service';
import { CancelAuthenticationService } from '../../multi-factor-authentication-adapter-service/cancel-authentication/cancel-authentication.service';
import { ContinueAuthenticationService } from '../../multi-factor-authentication-adapter-service/continue-authentication/continue-authentication.service';
import { InitiateAuthenticationService } from '../../multi-factor-authentication-adapter-service/initiate-authentication/initiate-authentication.service';
import { InitiateSsoSessionService } from '../../multi-factor-authentication-adapter-service/initiate-sso-session/initiate-sso-session.service';
import { InitiateUserRegistrationService } from '../../multi-factor-authentication-adapter-service/initiate-user-registration/initiate-user-registration.service';
import { SubmitEmailOtpService } from '../../multi-factor-authentication-adapter-service/submit-email-otp/submit-email-otp.service';
import { SubmitUserInformationService } from '../../multi-factor-authentication-adapter-service/submit-user-information/submit-user-information.service';
import { MfaUtilService } from '../../multi-factor-authentication-adapter-service/util-service/mfa-util.service';
import { PostWithFingerPrintService } from '../../remember-me-adapter-service/post-with-fingerprint/post-with-fingerprint-service';
import { SwapAccessTokenService } from '../../swap-access-token-service/swap-access-token-service';
import { FlyDeltaMethodService } from '../fly-delta-method/fly-delta-method.service';
import { SmsMethodService } from '../sms-method/sms-method.service';

@Injectable({
  providedIn: 'root'
})
export class NewUserMfaWrapperHelperSupportService {
  flowId = '';
  applicationId = '';
  applicationName = '';

  initiateSsoSession = inject(InitiateSsoSessionService);
  submitUserInformation = inject(SubmitUserInformationService);
  initiateUserRegistration = inject(InitiateUserRegistrationService);
  initiateAuthentication = inject(InitiateAuthenticationService);
  submitEmailOtp = inject(SubmitEmailOtpService);
  continueAuthentication = inject(ContinueAuthenticationService);
  addBackupMfa = inject(AddBackupMfaService);
  smsMethod = inject(SmsMethodService);
  flyDeltaMethod = inject(FlyDeltaMethodService);
  swapAccessToken = inject(SwapAccessTokenService);
  cancelAuthentication = inject(CancelAuthenticationService);
  multiFactorAuthenticationUtils = inject(
    MultiFactorAuthenticationUtilsService
  );

  postWithFingerPrintService = inject(PostWithFingerPrintService);
  mfaUtils = inject(MfaUtilService);

  registerAccount(
    loginOAuthParam: LoginOAuthParams,
    password: string,
    fieldValues: UserRegistrationInfo
  ): Observable<string | SubmitUserRegistration> {
    return this.submitUserInformation
      .submitUserInformation(
        loginOAuthParam.oauthConfig,
        this.flowId,
        password,
        fieldValues
      )
      .pipe(
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  initiateMfaWithoutEmailValidation(
    loginOAuthParams: LoginOAuthParams,
    flowId: string
  ): Observable<string | AddMfaBackup> {
    return this.initiateAuthentication
      .initiateAuthentication(loginOAuthParams.oauthConfig, flowId)
      .pipe(
        concatMap((response: InitiateAuthentication) => {
          return this.addBackupMfa.addBackupMfa(
            loginOAuthParams.oauthConfig,
            response?.id ? response.id : ''
          );
        }),
        tap((res: AddMfaBackup) => {
          this.assignApplicationIdName(res);
        }),
        catchError((error) => of(this.mfaUtils.getMfaRegistrationError(error)))
      );
  }

  finish(
    loginOAuthParams: LoginOAuthParams
  ): Observable<SwapAuthForAccessTokens> {
    if (this.smsMethod.authCode || this.flyDeltaMethod.authCode) {
      const swapAccessTokenObject: SwapAuthForAccessTokensParams = {
        authorizationCode:
          this.smsMethod.authCode || this.flyDeltaMethod.authCode,
        oauthConfig: loginOAuthParams.oauthConfig,
        pkcCodeGenerated: loginOAuthParams.pkceCode
      };
      return from(
        this.swapAccessToken.swapAuthForAccessTokens(
          swapAccessTokenObject,
          true
        )
      );
    }

    return this.cancelAuthentication
      .cancelAuthentication(loginOAuthParams.oauthConfig, this.flowId)
      .pipe(
        concatMap((initialResult) => {
          const swapToken: SwapAuthForAccessTokensParams = {
            authorizationCode: initialResult.authorizeResponse?.code,
            oauthConfig: loginOAuthParams.oauthConfig,
            pkcCodeGenerated: loginOAuthParams.pkceCode
          };
          return from(
            this.swapAccessToken.swapAuthForAccessTokens(swapToken, true)
          );
        })
      );
  }

  assignApplicationIdName(res: AddMfaBackup) {
    this.applicationName =
      res.devicePairingMethods?.find((device) => device.deviceType === 'MOBILE')
        ?.applicationName ?? '';
    this.applicationId =
      res.devicePairingMethods?.find((device) => device.deviceType === 'MOBILE')
        ?.applicationId ?? '';
  }
}
